import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import {
  ColumnConditionRhsArray,
  ColumnConditionRhsCommon,
  ColumnConditionRhsRange,
  ConditionArrayOperator,
  ConditionBooleanOperator,
  ConditionColorOperator,
  ConditionContentOperator,
  ConditionDateOperator,
  ConditionEqualityOperator,
  ConditionNumericOperator,
  ConditionTextOperator,
  DateTimeCustomOption,
  DefaultConditionOperator,
  isDateValid,
  isNumValid,
} from '@makyoapp/makyo-common'

// Initialize the isBetween plugin
dayjs.extend(isBetween)
dayjs.extend(timezone)
dayjs.extend(utc)

export const DEFAULT_TIMEZONE = dayjs.tz.guess()

/**
 * This function is used to convert a raw value to a string.
 * It handles null/undefined cases, Date objects, Location objects, and other types by converting them to strings.
 */
function parseRawLhsValueToString(value: unknown): string {
  try {
    // Handle null/undefined cases
    if (value === null || value === undefined) {
      return '__UNDEFINED__'
    }

    // Handle Date objects
    if (value instanceof Date) {
      try {
        return value.toISOString() ?? ''
      } catch (error) {
        console.error('Error in parseRawLhsValueToString:', error, { value })
        return ''
      }
    }

    // Handle Location objects (with Lat/Lon)
    if (
      typeof value === 'object' &&
      value !== null &&
      Object.prototype.hasOwnProperty.call(value, 'Lat') &&
      Object.prototype.hasOwnProperty.call(value, 'Lon')
    ) {
      const location = value as { Lat: number | string; Lon: number | string }
      return `${location.Lat}, ${location.Lon}`
    }

    // Handle string values
    if (typeof value === 'string') {
      return value
    }

    // Handle other types by converting to string
    return String(value)
  } catch (error) {
    console.error('Error in parseRawLhsValueToString:', error, { value })
    return '__UNDEFINED__'
  }
}

function isValueEmpty(value: unknown) {
  if (value === null || value === undefined) {
    return true
  }

  // date
  if (value instanceof Date) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim() === '' || value === '__UNDEFINED__'
  }
  if (Array.isArray(value) && value.length === 0) {
    return true
  }
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true
  }

  return false
}

/**
 * This function is used to retrieve the year, month, or full date from two given date objects based on the provided variant option.
 * The variant option can be current year, last year, next year, current month, next month, last month, today, tomorrow, yesterday, or undefined.
 */
function getValueToCompare(lhsDate: dayjs.Dayjs, rhsDate: dayjs.Dayjs, variant?: DateTimeCustomOption) {
  switch (variant) {
    case DateTimeCustomOption.CurrentYear:
    case DateTimeCustomOption.LastYear:
    case DateTimeCustomOption.NextYear: {
      return { lhs: lhsDate.year(), rhs: rhsDate.year() }
    }
    case DateTimeCustomOption.CurrentMonth:
    case DateTimeCustomOption.NextMonth:
    case DateTimeCustomOption.LastMonth: {
      // Compare both year and month together
      return {
        lhs: lhsDate.year() * 12 + lhsDate.month(),
        rhs: rhsDate.year() * 12 + rhsDate.month(),
      }
    }
    case DateTimeCustomOption.Today:
    case DateTimeCustomOption.Tomorrow:
    case DateTimeCustomOption.Yesterday:
    default:
      // Normalize to start of day in UTC and return timestamp for comparison
      return {
        lhs: lhsDate.startOf('day').valueOf(),
        rhs: rhsDate.startOf('day').valueOf(),
      }
  }
}

/**
 * This function is used to get the boolean value of a given value.
 * It handles boolean values, string values (true/false), and null/undefined values.
 */
function getBooleanValue(value: unknown) {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    if (value.toLocaleLowerCase() === 'true') return true
    if (value.toLocaleLowerCase() === 'false') return false
  }
  return false
}

// ========================================================
// Condition Parsers
// ========================================================

function parseDateCondition(
  lhsRawValue: unknown,
  op: ConditionDateOperator,
  rhsRawValue?: ColumnConditionRhsCommon | ColumnConditionRhsRange,
  variant?: DateTimeCustomOption,
  tz?: string
) {
  const timezone = tz === 'auto' ? DEFAULT_TIMEZONE : tz

  const now = dayjs.tz(dayjs.utc(), timezone)

  // parse date value based on table timezone setting
  const toDayJs = (dateStr: string) => {
    const sourceDate = dayjs.utc(dateStr)
    return sourceDate.tz(timezone)
  }

  const lhsValue = parseRawLhsValueToString(lhsRawValue)
  const rhsValue = (rhsRawValue as ColumnConditionRhsCommon)?.value ?? ''

  const lhsDayJs = toDayJs(lhsValue)
  const rhsDayJs = toDayJs(rhsValue)

  const lhsDate = lhsDayJs.toDate()
  const rhsDate = rhsDayJs.toDate()
  const rhsStartDate = toDayJs((rhsRawValue as ColumnConditionRhsRange)?.start).toDate()
  const rhsEndDate = toDayJs((rhsRawValue as ColumnConditionRhsRange)?.end).toDate()

  // For date comparison, normalize all dates to start of day using UTC
  const normalizeDate = (date: Date) => {
    return dayjs.utc(date).startOf('day').toDate()
  }

  // For IsBetween/IsNotBetween with year range
  if ((op === ConditionDateOperator.IsBetween || op === ConditionDateOperator.IsNotBetween) && !isDateValid(lhsDate)) {
    return false
  }

  // Special handling for year ranges
  if (
    (op === ConditionDateOperator.IsBetween || op === ConditionDateOperator.IsNotBetween) &&
    (variant === DateTimeCustomOption.CurrentYear ||
      variant === DateTimeCustomOption.NextYear ||
      variant === DateTimeCustomOption.LastYear)
  ) {
    const currentYear = now.year()
    const lastYear = currentYear - 1
    const nextYear = currentYear + 1

    let startOfRange: dayjs.Dayjs
    let endOfRange: dayjs.Dayjs

    // Check if start and end dates are the same (single year) or different (year range)
    const isSingleYear =
      (rhsRawValue as ColumnConditionRhsRange)?.start === (rhsRawValue as ColumnConditionRhsRange)?.end

    if (isSingleYear) {
      // Handle single year cases
      if (variant === DateTimeCustomOption.LastYear) {
        startOfRange = dayjs.utc().year(lastYear).startOf('year')
        endOfRange = dayjs.utc().year(lastYear).endOf('year')
      } else if (variant === DateTimeCustomOption.CurrentYear) {
        startOfRange = dayjs.utc().year(currentYear).startOf('year')
        endOfRange = dayjs.utc().year(currentYear).endOf('year')
      } else {
        // NextYear
        startOfRange = dayjs.utc().year(nextYear).startOf('year')
        endOfRange = dayjs.utc().year(nextYear).endOf('year')
      }
    } else {
      // Handle year range cases
      const startVariant = variant
      const endVariant =
        (rhsRawValue as ColumnConditionRhsRange)?.end === (rhsRawValue as ColumnConditionRhsRange)?.start
          ? startVariant
          : (rhsRawValue as ColumnConditionRhsRange)?.end === 'current_year'
            ? DateTimeCustomOption.CurrentYear
            : (rhsRawValue as ColumnConditionRhsRange)?.end === 'next_year'
              ? DateTimeCustomOption.NextYear
              : (rhsRawValue as ColumnConditionRhsRange)?.end === 'last_year'
                ? DateTimeCustomOption.LastYear
                : dayjs.utc((rhsRawValue as ColumnConditionRhsRange)?.end).year() > currentYear
                  ? DateTimeCustomOption.NextYear
                  : dayjs.utc((rhsRawValue as ColumnConditionRhsRange)?.end).year() < currentYear
                    ? DateTimeCustomOption.LastYear
                    : DateTimeCustomOption.CurrentYear

      // Get the actual years for comparison
      const startYear =
        startVariant === DateTimeCustomOption.LastYear
          ? lastYear
          : startVariant === DateTimeCustomOption.CurrentYear
            ? currentYear
            : nextYear

      const endYear =
        endVariant === DateTimeCustomOption.LastYear
          ? lastYear
          : endVariant === DateTimeCustomOption.CurrentYear
            ? currentYear
            : nextYear

      // For IsNotBetween, check if the date is before the range or after the range
      if (op === ConditionDateOperator.IsNotBetween) {
        const dateYear = dayjs.utc(normalizeDate(lhsDate)).year()
        return dateYear < startYear || dateYear > endYear
      }

      // For IsBetween, check if the date's year is within the range (inclusive)
      const dateYear = dayjs.utc(normalizeDate(lhsDate)).year()
      return dateYear >= startYear && dateYear <= endYear
    }

    // For IsNotBetween, check if the date is before the range or after the range
    if (op === ConditionDateOperator.IsNotBetween) {
      const normalizedDate = dayjs.utc(normalizeDate(lhsDate))

      // Check if date is strictly before start or strictly after end
      return normalizedDate.isBefore(startOfRange, 'day') || normalizedDate.isAfter(endOfRange, 'day')
    }

    // For IsBetween, check if the date is within the range (inclusive)
    return dayjs.utc(normalizeDate(lhsDate)).isBetween(startOfRange, endOfRange, 'day', '[]')
  }

  // For IsBetween/IsNotBetween with CurrentMonth/LastMonth variant (single month)
  if (
    (op === ConditionDateOperator.IsBetween || op === ConditionDateOperator.IsNotBetween) &&
    (variant === DateTimeCustomOption.CurrentMonth || variant === DateTimeCustomOption.LastMonth) &&
    (rhsRawValue as ColumnConditionRhsRange)?.end === (rhsRawValue as ColumnConditionRhsRange)?.start
  ) {
    if (variant === DateTimeCustomOption.CurrentMonth) {
      // For current month, check if date falls within the entire month
      const startOfMonth = now.startOf('month')
      const endOfMonth = now.endOf('month')
      const isWithinMonth = dayjs.utc(normalizeDate(lhsDate)).isBetween(startOfMonth, endOfMonth, 'day', '[]')

      return op === ConditionDateOperator.IsBetween ? isWithinMonth : !isWithinMonth
    }

    // Handle last month
    const lastMonth = now.subtract(1, 'month')
    const startOfMonth = lastMonth.startOf('month')
    const endOfMonth = lastMonth.endOf('month')

    const isWithinTargetMonth = dayjs.utc(normalizeDate(lhsDate)).isBetween(startOfMonth, endOfMonth, 'day', '[]')

    return op === ConditionDateOperator.IsBetween ? isWithinTargetMonth : !isWithinTargetMonth
  }

  const { lhs, rhs } = getValueToCompare(lhsDayJs, rhsDayJs, variant)

  switch (op) {
    case ConditionDateOperator.IsEmpty:
      return isValueEmpty(lhsValue)
    case ConditionDateOperator.IsNotEmpty:
      return !isValueEmpty(lhsValue)
    case ConditionDateOperator.IsEqual:
      if (!isDateValid(lhsDate) && !isDateValid(rhsDate)) {
        return true
      }
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return false
      }

      return lhs === rhs
    case ConditionDateOperator.IsNotEqual:
      if (!isDateValid(lhsDate) && !isDateValid(rhsDate)) {
        return false
      }
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return true
      }
      return lhs !== rhs
    case ConditionDateOperator.IsBefore:
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return false
      }

      return lhs < rhs
    case ConditionDateOperator.OnOrBefore:
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return false
      }
      return lhs <= rhs

    case ConditionDateOperator.IsAfter:
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return false
      }
      return lhs > rhs
    case ConditionDateOperator.OnOrAfter:
      if (!isDateValid(lhsDate) || !isDateValid(rhsDate)) {
        return false
      }
      return lhs >= rhs
    case ConditionDateOperator.IsBetween: {
      if (!isDateValid(lhsDate) || !isDateValid(rhsStartDate) || !isDateValid(rhsEndDate)) {
        return false
      }

      // Normalize all dates to start of day for comparison using UTC
      const normalizedLhsDate = dayjs.utc(normalizeDate(lhsDate))
      let normalizedStartDate = dayjs.utc(normalizeDate(rhsStartDate))
      let normalizedEndDate = dayjs.utc(normalizeDate(rhsEndDate))

      // If start date is after end date, swap them
      if (normalizedStartDate.isAfter(normalizedEndDate)) {
        ;[normalizedStartDate, normalizedEndDate] = [normalizedEndDate, normalizedStartDate]
      }

      return normalizedLhsDate.isBetween(normalizedStartDate, normalizedEndDate, 'day', '[]')
    }
    case ConditionDateOperator.IsNotBetween:
      if (!isDateValid(lhsDate) || !isDateValid(rhsStartDate) || !isDateValid(rhsEndDate)) {
        return false
      }
      if (dayjs.utc(rhsStartDate).isAfter(dayjs.utc(rhsEndDate))) {
        return false
      }
      return !dayjs.utc(lhsDate).isBetween(dayjs.utc(rhsStartDate), dayjs.utc(rhsEndDate), 'day', '[]')
  }
}

function parseNumericCondition(
  lhsRawValue: unknown,
  op: ConditionNumericOperator,
  rhsRawValue?: ColumnConditionRhsCommon | ColumnConditionRhsRange
) {
  const lhsValue = typeof lhsRawValue === 'number' ? lhsRawValue.toString() : lhsRawValue

  const rhsValue = (rhsRawValue as ColumnConditionRhsCommon)?.value?.toString() ?? ''
  const rhsStartValue = (rhsRawValue as ColumnConditionRhsRange)?.start?.toString() ?? ''
  const rhsEndValue = (rhsRawValue as ColumnConditionRhsRange)?.end?.toString() ?? ''
  const lhsNum = Number(lhsValue)
  const rhsNum = Number(rhsValue)
  const rhsStartNum = Number(rhsStartValue)
  const rhsEndNum = Number(rhsEndValue)
  const isRangeValid = isNumValid(lhsValue) && isNumValid(rhsStartNum) && isNumValid(rhsEndNum)
  const isValid = isNumValid(lhsValue) && isNumValid(rhsValue)

  // Helper function to check if numbers are equal within rounding
  const areNumbersEqual = (a: number, b: number) => {
    return Math.abs(Math.round(a) - b) < 0.01 || Math.abs(a - b) < 0.01
  }

  switch (op) {
    case ConditionNumericOperator.IsEqual:
      if (!isValid) {
        return false
      }
      return areNumbersEqual(lhsNum, rhsNum)
    case ConditionNumericOperator.IsNotEqual:
      if (!isValid) {
        return false
      }
      return !areNumbersEqual(lhsNum, rhsNum)
    case ConditionNumericOperator.IsEmpty:
      return (typeof lhsValue === 'string' && lhsValue.trim() === '') || lhsValue === undefined || lhsValue === null
    case ConditionNumericOperator.IsNotEmpty:
      return typeof lhsValue === 'string' && lhsValue.trim() !== '' && lhsValue !== undefined && lhsValue !== null
    case ConditionNumericOperator.IsBetween:
      if (!isRangeValid) {
        return false
      }
      // For between, we'll use the actual numbers without rounding
      return lhsNum >= rhsStartNum && lhsNum <= rhsEndNum
    case ConditionNumericOperator.IsNotBetween:
      if (!isRangeValid) {
        return false
      }
      // For between, we'll use the actual numbers without rounding
      return !(lhsNum >= rhsStartNum && lhsNum <= rhsEndNum)
    case ConditionNumericOperator.GreaterThan:
      if (!isValid) {
        return false
      }
      return lhsNum > rhsNum
    case ConditionNumericOperator.GreaterThanOrEqual:
      if (!isValid) {
        return false
      }
      return lhsNum >= rhsNum || areNumbersEqual(lhsNum, rhsNum)
    case ConditionNumericOperator.LessThan:
      if (!isValid) {
        return false
      }
      return lhsNum < rhsNum
    case ConditionNumericOperator.LessThanOrEqual:
      if (!isValid) {
        return false
      }
      return lhsNum <= rhsNum || areNumbersEqual(lhsNum, rhsNum)
  }
}

function parseColorCondition(
  lhsRawValue: unknown,
  op: ConditionColorOperator,
  rhsRawValue?: ColumnConditionRhsCommon
): boolean {
  // Handle null/undefined cases first
  if (op === ConditionColorOperator.IsEmpty) {
    return !lhsRawValue || lhsRawValue === ''
  }
  if (op === ConditionColorOperator.IsNotEmpty) {
    return !!lhsRawValue && lhsRawValue !== ''
  }

  // Parse and validate colors
  let lhsValue = parseRawLhsValueToString(lhsRawValue)
  let rhsValue = (rhsRawValue?.value ?? '').toString()

  // For equality checks, we need both values for comparison
  if ((op === ConditionColorOperator.IsEqual || op === ConditionColorOperator.IsNotEqual) && (!lhsValue || !rhsValue)) {
    return false
  }

  try {
    // Normalize and validate colors
    const normalizeColor = (color: string): string | null => {
      // Remove whitespace
      color = color.trim().toLowerCase()

      // Handle hex colors
      const hexRegex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/
      if (hexRegex.test(color)) {
        // Ensure # prefix
        if (!color.startsWith('#')) {
          color = '#' + color
        }
        // Convert 3-digit hex to 6-digit
        if (color.length === 4) {
          color = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3]
        }
        return color
      }

      // Handle rgb/rgba colors
      const rgbRegex = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)$/
      const rgbMatch = color.match(rgbRegex)
      if (rgbMatch) {
        const [, r, g, b] = rgbMatch
        // Convert to hex
        const toHex = (n: string) => {
          const hex = parseInt(n).toString(16)
          return hex.length === 1 ? '0' + hex : hex
        }
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`
      }

      // Handle named colors using canvas
      const ctx = document.createElement('canvas').getContext('2d')
      if (ctx) {
        ctx.fillStyle = color
        if (ctx.fillStyle !== '#000000') {
          return ctx.fillStyle
        }
      }

      return null
    }

    const normalizedLhs = normalizeColor(lhsValue)
    const normalizedRhs = normalizeColor(rhsValue)

    // If either color is invalid, return false
    if (!normalizedLhs || !normalizedRhs) {
      return false
    }

    // Compare normalized color values
    switch (op) {
      case ConditionColorOperator.IsEqual:
        return normalizedLhs === normalizedRhs
      case ConditionColorOperator.IsNotEqual:
        return normalizedLhs !== normalizedRhs
      default:
        return false
    }
  } catch (error) {
    console.error('Color parsing error:', error)
    return false
  }
}

function parseTextCondition(
  lhsRawValue: unknown,
  op: ConditionTextOperator,
  rhsRawValue?: ColumnConditionRhsCommon | ColumnConditionRhsArray,
  caseSensitive: boolean | undefined = false
) {
  const lhsValue = parseRawLhsValueToString(lhsRawValue)
  const rhsValue = rhsRawValue?.value ?? ''
  let arrayRhsValue: string[] = []
  if (!Array.isArray(rhsValue)) {
    arrayRhsValue = rhsValue.split(',').map((i) => i.trim())
  } else {
    arrayRhsValue = rhsValue
  }

  switch (op) {
    case ConditionTextOperator.BeginsWith:
      return caseSensitive
        ? lhsValue.startsWith(rhsValue.toString())
        : lhsValue.toLowerCase().startsWith(rhsValue.toString().toLowerCase())
    case ConditionTextOperator.EndsWith:
      return caseSensitive
        ? lhsValue.endsWith(rhsValue.toString())
        : lhsValue.toLowerCase().endsWith(rhsValue.toString().toLowerCase())
    case ConditionTextOperator.IsEqual:
      return caseSensitive
        ? String(lhsValue).trim() === String(rhsValue).trim()
        : String(lhsValue).trim().toLowerCase() === String(rhsValue).trim().toLowerCase()
    case ConditionTextOperator.IsNotEqual:
      return caseSensitive
        ? String(lhsValue).trim() !== String(rhsValue).trim()
        : String(lhsValue).trim().toLowerCase() !== String(rhsValue).trim().toLowerCase()
    case ConditionTextOperator.IsEmpty:
      return isValueEmpty(lhsValue)
    case ConditionTextOperator.IsNotEmpty:
      return !isValueEmpty(lhsValue)
    case ConditionTextOperator.Contains:
      if (!rhsValue) {
        return !lhsValue || lhsValue === ''
      }
      return caseSensitive
        ? !Array.isArray(rhsValue)
          ? lhsValue.includes(rhsValue)
          : (rhsValue as string[]).some((rv) => lhsValue.includes(rv))
        : !Array.isArray(rhsValue)
          ? lhsValue.toLowerCase().includes(rhsValue.toLowerCase())
          : rhsValue.some((rv) => lhsValue.toLowerCase().includes(rv.toLowerCase()))
    case ConditionTextOperator.DoesNotContain:
      if (!rhsValue) {
        return lhsValue && lhsValue !== ''
      }
      return caseSensitive
        ? !Array.isArray(rhsValue)
          ? !lhsValue.includes(rhsValue)
          : !rhsValue.some((rv) => lhsValue.includes(rv))
        : !Array.isArray(rhsValue)
          ? !lhsValue.toLowerCase().includes(rhsValue.toLowerCase())
          : !rhsValue.some((rv) => lhsValue.toLowerCase().includes(rv.toLowerCase()))
    case ConditionTextOperator.IsContainedIn: {
      // Handle empty values
      if (!lhsValue) return false
      if (!arrayRhsValue.length) return false

      // Always case-insensitive normalization
      const normalize = (value: string) => value.trim().toLowerCase()

      const normalizedLhs = normalize(lhsValue.toString())
      const normalizedRhs = arrayRhsValue.map((v) => normalize(v.toString()))

      // Check if LHS value is contained in RHS array
      return normalizedRhs.includes(normalizedLhs)
    }
    case ConditionTextOperator.IsNotContainedIn: {
      // Handle empty values
      if (!lhsValue) return true
      if (!arrayRhsValue.length) return true

      // Always case-insensitive normalization
      const normalize = (value: string) => value.trim().toLowerCase()

      const normalizedLhs = normalize(lhsValue.toString())
      const normalizedRhs = arrayRhsValue.map((v) => normalize(v.toString()))

      // Check if LHS value is NOT contained in RHS array
      return !normalizedRhs.includes(normalizedLhs)
    }
  }
}

function parseBooleanCondition(lhsRawValue: unknown, op: ConditionBooleanOperator) {
  const lhsValue = getBooleanValue(lhsRawValue)
  switch (op) {
    case ConditionBooleanOperator.IsFalse:
      return !lhsValue
    case ConditionBooleanOperator.IsTrue:
      return lhsValue
  }
}

function parseArrayCondition(
  lhsValue: string[],
  op: ConditionArrayOperator,
  rhsRawValue?: ColumnConditionRhsCommon,
  caseSensitive: boolean | undefined = false
) {
  if (!Array.isArray(lhsValue)) {
    return false
  }

  const rhsValue = rhsRawValue?.value || ''
  switch (op) {
    case ConditionArrayOperator.Contains:
      return caseSensitive
        ? lhsValue.includes(rhsValue)
        : lhsValue.map((v) => v.toLowerCase()).includes(rhsValue.toLowerCase())
    case ConditionArrayOperator.DoesNotContain:
      return caseSensitive
        ? !lhsValue.includes(rhsValue)
        : lhsValue.map((v) => v.toLowerCase()).includes(rhsValue.toLowerCase())
    case ConditionArrayOperator.IsEmpty:
      return isValueEmpty(lhsValue)
    case ConditionArrayOperator.IsNotEmpty:
      return !isValueEmpty(lhsValue)
  }
}

function parseEqualityCondition(
  lhsRawValue: unknown,
  op: ConditionEqualityOperator,
  rhsRawValue?: ColumnConditionRhsCommon
) {
  const lhsValue = parseRawLhsValueToString(lhsRawValue)
  const rhsValue = rhsRawValue?.value ?? ''
  switch (op) {
    case ConditionEqualityOperator.IsEqual:
      return lhsValue === rhsValue
    case ConditionEqualityOperator.IsNotEqual:
      return lhsValue !== rhsValue
  }
}

function parseContentCondition(lhsRawValue: unknown, op: ConditionContentOperator) {
  const isArr = Array.isArray(lhsRawValue)
  const lhsValue = isArr ? lhsRawValue : parseRawLhsValueToString(lhsRawValue)
  switch (op) {
    case ConditionContentOperator.IsEmpty:
      return isArr ? lhsValue.length === 0 : lhsValue === ''
    case ConditionContentOperator.IsNotEmpty:
      return isArr ? lhsValue.length > 0 : lhsValue !== ''
  }
}

// ========================================================
// Main parser function to parse a condition based on the operator.
// ========================================================

/**
 * Used mainly in filtering / visibility area.
 * Handles different types of operators and their corresponding right-hand side values.
 */
export function parseConditionBasedOnOperator(
  lhsValue: unknown,
  op: DefaultConditionOperator,
  rhsRawValue?: ColumnConditionRhsCommon | ColumnConditionRhsRange | ColumnConditionRhsArray,
  caseSensitive?: boolean,
  rhsVariant?: DateTimeCustomOption,
  timezone?: string
): boolean {
  switch (op) {
    case ConditionColorOperator.IsEqual:
    case ConditionColorOperator.IsNotEqual:
    case ConditionColorOperator.IsEmpty:
    case ConditionColorOperator.IsNotEmpty:
      // case ConditionColorOperator.BeginsWith:
      // case ConditionColorOperator.EndsWith:
      // case ConditionColorOperator.Contains:
      // case ConditionColorOperator.DoesNotContain:
      return parseColorCondition(lhsValue as string, op, rhsRawValue as ColumnConditionRhsCommon)
    /**
     * text
     */
    case ConditionTextOperator.IsEqual:
    case ConditionTextOperator.IsNotEqual:
    case ConditionTextOperator.IsEmpty:
    case ConditionTextOperator.IsNotEmpty:
    case ConditionTextOperator.BeginsWith:
    case ConditionTextOperator.EndsWith:
    case ConditionTextOperator.Contains:
    case ConditionTextOperator.DoesNotContain:
    case ConditionTextOperator.IsContainedIn:
    case ConditionTextOperator.IsNotContainedIn: {
      const result = parseTextCondition(lhsValue as string, op, rhsRawValue as ColumnConditionRhsCommon, caseSensitive)
      return Boolean(result)
    }
    /**
     * array
     */
    case ConditionArrayOperator.Contains:
    case ConditionArrayOperator.DoesNotContain:
    case ConditionArrayOperator.IsEmpty:
    case ConditionArrayOperator.IsNotEmpty:
      return parseArrayCondition(lhsValue as string[], op, rhsRawValue as ColumnConditionRhsCommon, caseSensitive)
    /**
     * boolean
     */
    case ConditionBooleanOperator.IsTrue:
    case ConditionBooleanOperator.IsFalse:
      return parseBooleanCondition(lhsValue, op)
    /**
     * equality
     */
    case ConditionEqualityOperator.IsEqual:
    case ConditionEqualityOperator.IsNotEqual:
      return parseEqualityCondition(lhsValue, op, rhsRawValue as ColumnConditionRhsCommon)
    /**
     * content
     */
    case ConditionContentOperator.IsEmpty:
    case ConditionContentOperator.IsNotEmpty:
      return parseContentCondition(lhsValue, op)
    /**
     * date
     */
    case ConditionDateOperator.IsEqual:
    case ConditionDateOperator.IsNotEqual:
    case ConditionDateOperator.IsEmpty:
    case ConditionDateOperator.IsNotEmpty:
    case ConditionDateOperator.IsBefore:
    case ConditionDateOperator.IsAfter:
    case ConditionDateOperator.IsBetween:
    case ConditionDateOperator.IsNotBetween:
    case ConditionDateOperator.OnOrBefore:
    case ConditionDateOperator.OnOrAfter:
      return parseDateCondition(lhsValue, op, rhsRawValue as ColumnConditionRhsCommon, rhsVariant, timezone)
    /**
     * numeric
     */
    case ConditionNumericOperator.IsEqual:
    case ConditionNumericOperator.IsNotEqual:
    case ConditionNumericOperator.GreaterThan:
    case ConditionNumericOperator.GreaterThanOrEqual:
    case ConditionNumericOperator.LessThan:
    case ConditionNumericOperator.LessThanOrEqual:
    case ConditionNumericOperator.IsEmpty:
    case ConditionNumericOperator.IsNotEmpty:
    case ConditionNumericOperator.IsBetween:
    case ConditionNumericOperator.IsNotBetween:
      return parseNumericCondition(lhsValue, op, rhsRawValue as ColumnConditionRhsCommon)
  }
}
